import { QuestionInterface } from "../../domain/interfaces/question_interface";
import { BooleanQuestionStatisticsInterface, NumberQuestionStatisticsInterface, QuestionStatisticsInterface, StringQuestionStatisticsInterface } from "../../domain/interfaces/question_stat_interface";
import { SurveyInterface } from "../../domain/interfaces/survey_interface";
import { SurveyRepository } from "../../domain/repository/survey_repository";
import { SurveyApiService } from "../data_source/survey_api_service";


export class SurveyRepositoryImpl implements SurveyRepository {
    private surveyApiService: SurveyApiService;

    constructor() {
        this.surveyApiService = new SurveyApiService();
    }

    async getSurveys(skip: number, limit: number): Promise<{ surveys: SurveyInterface[], fetchedAll: boolean }> {
        const response = await this.surveyApiService.getSurveys(skip, limit);
        if (response.status === 200) {
            const surveys: SurveyInterface[] = [];
            for (const survey of response.data.objects) {
                if (survey.active === true) {
                    const questions: QuestionInterface[] = [];
                    let pointsLeft = 0;
                    for (const question of survey.questions) {
                        const initialAnswer = question.answer === undefined ? undefined : {
                            id: question.answer._id,
                            questionId: question._id,
                            answer: question.answer.answer,
                            text: question.answer.text || "",
                        };
                        questions.push({
                            initialAnswer: initialAnswer,
                            id: question._id,
                            question: question.text,
                            type: question.answerType,
                        });
                        if (initialAnswer === undefined) {
                            pointsLeft += 1;
                        }
                    }

                    surveys.push({
                        id: survey._id,
                        title: survey.title,
                        description: survey.description,
                        nbAnswers: survey.nbAnswers,
                        expectedRespondents: survey.expectedRespondents,
                        startDate: new Date(survey.startDate),
                        endDate: new Date(survey.endDate),
                        answered: survey.answered,
                        questions: questions,
                        nbQuestions: questions.length,
                        nbPointsLeft: pointsLeft,
                        surveyComment: survey.comment?.answer,
                        isResultPublic: survey.isResultPublic,
                    });
                }
            }
            return { surveys, fetchedAll: response.data.pages <= (skip / limit) + 1 };
        }
        throw new Error("Failed to fetch surveys");
    }

    async getSurveyById(surveyId: string): Promise<SurveyInterface | null> {
        const response = await this.surveyApiService.getSurveyById(surveyId);
        if (response.status === 200) {
            const survey = response.data;
            if (survey.active === true) {
                const questions: QuestionInterface[] = [];
                let pointsLeft = 0;
                for (const question of survey.questions) {
                    const initialAnswer = question.answer === undefined ? undefined : {
                        id: question.answer._id,
                        questionId: question._id,
                        answer: question.answer.answer,
                        text: question.answer.text || "",
                    };
                    questions.push({
                        initialAnswer: initialAnswer,
                        id: question._id,
                        question: question.text,
                        type: question.answerType,
                    });
                    if (initialAnswer === undefined) {
                        pointsLeft += 1;
                    }
                }

                return({
                    id: survey._id,
                    title: survey.title,
                    description: survey.description,
                    nbAnswers: survey.nbAnswers,
                    expectedRespondents: survey.expectedRespondents,
                    startDate: new Date(survey.startDate),
                    endDate: new Date(survey.endDate),
                    answered: survey.answered,
                    questions: questions,
                    nbQuestions: questions.length,
                    nbPointsLeft: pointsLeft,
                    surveyComment: survey.comment?.answer,
                    isResultPublic: survey.isResultPublic,
                });
            }
        }
        return null;
    }

    async getSurveyStatistics(surveyId: string): Promise<QuestionStatisticsInterface[] | null> {
        const response = await this.surveyApiService.getSurveyStatistics(surveyId);
        if (response.status === 200) {
            const stats: QuestionStatisticsInterface[] = [];
            for (const stat of response.data.questionStatistics) {
                switch (stat.answerType) {
                    case "boolean":
                        stats.push({
                            type: "boolean",
                            questionId: stat.questionId,
                            questionText: stat.questionText,
                            totalResponses: stat.totalResponses,
                            trueCount: stat.trueCount,
                            falseCount: stat.falseCount,
                            truePercentage: stat.truePercentage,
                            falsePercentage: stat.falsePercentage,
                        } as BooleanQuestionStatisticsInterface);
                        break;
                    case "number":
                        stats.push({
                            type: "number",
                            questionId: stat.questionId,
                            questionText: stat.questionText,
                            totalResponses: stat.totalResponses,
                            average: stat.average,
                        } as NumberQuestionStatisticsInterface);
                        break;
                    case "string":
                        stats.push({
                            type: "string",
                            questionId: stat.questionId,
                            questionText: stat.questionText,
                            totalResponses: stat.totalResponses,
                            responseCount: stat.responseCount,
                            sampleResponses: stat.sampleResponses ?? [],
                        } as StringQuestionStatisticsInterface);
                        break;
                }
            }
            return stats;
        }
        return null;
    }
}